// src/components/ManageDealDrawer/Sections/FormSection.tsx

import React, { useEffect } from 'react';
import { AUDITED_COLUMN_HEADING, DEAL_DRAWER_ICON_MAP, DEBT_PRODUCT_FILTERS, EXCLUDED_LABEL_TYPES } from '../../DealDrawerConstants';
import { InputRenderer } from './InputRenderer';
import { DynamicFieldsRenderer } from './DynamicFieldsRenderer';
import styles from '../../ManageDealDrawer.module.scss';
import { validateFormSection } from '../../../DealApplicationDrawer/customHooks/FormValidations';
import infoIcon from '../../../../assets/info.svg';
import Tooltip from '../../../_custom/Tooltip/Tooltip';

interface FormSectionProps {
    section: any;
    step: number;
    sectionIndex: number;
    totalSteps: number;
    formData: Record<string, any>;
    handleChange: (key: string, value: any, importanceKey?: string | null) => void;
    dealDrawerState: any;
    lenderList: { id: string; name: string }[];
    loanTypeOptions: Record<string, any>;
    sectorList: any[];
    negativeSectorList: any[];
    source: string;
    setDisablePrimary: (value: boolean) => void;
    auditedfinancialsMapping: Record<string, { year?: number; value: string }>;
    isWebsiteValid: boolean;
    setIsWebsiteValid: (value: boolean) => void;
    getInputValidation: (value: string, key: string) => boolean;
    getButtonIcon: (text: string) => JSX.Element;
    getButtonText: () => string;
    validateWebsite: (value: string) => void;
    isWebsitePrefilled: boolean;
    negativeLenderList: any[];
    getConvertedValue: (value: string, unit: string) => string;
    citiesList?: { label: string; value: string }[];
    activeFinancialsTab?: string;
    handleFinancialsTabChange?: (tab: string) => void;
    sectionDualDataStorage?: boolean;
}

export const FormSection: React.FC<FormSectionProps> = ({
    section,
    step,
    sectionIndex,
    source,
    totalSteps,
    formData,
    handleChange,
    dealDrawerState,
    sectorList,
    negativeSectorList,
    loanTypeOptions,
    lenderList,
    setDisablePrimary,
    auditedfinancialsMapping,
    isWebsiteValid,
    getInputValidation,
    getButtonIcon,
    getButtonText,
    validateWebsite,
    setIsWebsiteValid,
    isWebsitePrefilled,
    negativeLenderList,
    getConvertedValue,
    citiesList,
    activeFinancialsTab,
    handleFinancialsTabChange,
    sectionDualDataStorage,
}) => {
    // Check if the section is valid when required
    useEffect(() => {
        if (!section) return;
        const validationResult = validateFormSection(section, formData);
        const shouldDisable = !validationResult.isValid;
        setDisablePrimary(shouldDisable);
    }, [formData, section, setDisablePrimary]);

    return (
        <div className={styles.Section}>
            {section?.text && (
                <div className={styles.SectionHeading}>
                    {/* Show the step number only on the first section within that step */}
                    {sectionIndex === 0 && `${step}.`} {section?.text}
                </div>
            )}

            {section.sectionNote &&
                (() => {
                    const note = section.sectionNote;
                    return (
                        <div
                            className={styles.SectionNote}
                            style={{ backgroundColor: typeof note === 'object' ? note.backgroundColor ?? undefined : undefined }}
                        >
                            {typeof note === 'object' ? (
                                <>
                                    {note.icon && DEAL_DRAWER_ICON_MAP[note.icon] && (
                                        <img src={DEAL_DRAWER_ICON_MAP[note.icon]} alt="icon" className={styles.SectionNoteIcon} />
                                    )}

                                    <span className={styles.SectionNoteText}>
                                        {note.prefix && <span className={styles.SectionNoteTextPrefix}>{note.prefix} </span>}
                                        {note.message}
                                    </span>
                                </>
                            ) : (
                                <span className={styles.SectionNoteText}>{note}</span>
                            )}
                        </div>
                    );
                })()}

            <div className={styles.InputList}>
                {section?.columnHeading && section.columnHeading.length > 0 && (
                    <div className={styles.ColumnHeadingsRow}>
                        {/* Empty space for InputLabel column */}
                        <div className={styles.InputLabel}></div>

                        {/* Column headings container */}
                        <div className={styles.ColumnHeadings}>
                            {section.columnHeading.map((heading: string, index: number) => (
                                <div
                                    key={`column-heading-${index}`}
                                    className={styles.ColumnHeading}
                                    style={{
                                        width: `${100 / section.columnHeading.length}%`,
                                    }}
                                >
                                    {heading === AUDITED_COLUMN_HEADING
                                        ? `${heading} (${auditedfinancialsMapping['financialYear']?.value})`
                                        : heading}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {Object.keys(section?.config)?.map((key: string) => {
                    const fieldConfig = section.config[key];
                    const sectionKey = section.sectionKey;
                    const formKey = sectionKey ? `${sectionKey}.${key}` : key;
                    const hasTooltip = !!fieldConfig.tooltip;

                    return (
                        <React.Fragment key={`input-${key}`}>
                            {/* Main field */}
                            <div
                                style={source === DEBT_PRODUCT_FILTERS ? { borderBottom: 'none', marginTop: '0' } : {}}
                                className={styles.InputItem}
                            >
                                {!EXCLUDED_LABEL_TYPES.includes(fieldConfig?.type) && source !== DEBT_PRODUCT_FILTERS && (
                                    <div className={styles.InputLabel} style={{ display: 'block' }}>
                                        {fieldConfig.label}
                                        {fieldConfig.secondaryLabel && (
                                            <span className={styles.SecondaryLabel}> {fieldConfig.secondaryLabel}</span>
                                        )}
                                        {hasTooltip && (
                                            <Tooltip content={fieldConfig.tooltip} direction="top" fitContent>
                                                <img src={infoIcon} alt="info" className={styles.InfoIcon} />
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                                <div style={{ width: '100%' }}>
                                    <InputRenderer
                                        sectionKey={section.sectionKey}
                                        setDisablePrimary={setDisablePrimary}
                                        fieldKey={key}
                                        config={fieldConfig}
                                        formData={formData}
                                        handleChange={handleChange}
                                        dealDrawerState={dealDrawerState}
                                        lenderList={lenderList}
                                        loanTypeOptions={loanTypeOptions}
                                        sectorList={sectorList}
                                        negativeSectorList={negativeSectorList}
                                        source={source}
                                        step={step}
                                        auditedfinancialsMapping={auditedfinancialsMapping}
                                        isWebsiteValid={isWebsiteValid}
                                        setIsWebsiteValid={setIsWebsiteValid}
                                        getInputValidation={getInputValidation}
                                        getButtonIcon={getButtonIcon}
                                        getButtonText={getButtonText}
                                        validateWebsite={validateWebsite}
                                        isWebsitePrefilled={isWebsitePrefilled}
                                        negativeLenderList={negativeLenderList}
                                        getConvertedValue={getConvertedValue}
                                        showFloatingLabel={false}
                                        citiesList={citiesList}
                                        sectionDualDataStorage={section?.dualDataStorage}
                                        activeFinancialsTab={activeFinancialsTab}
                                        handleFinancialsTabChange={handleFinancialsTabChange}
                                    />
                                </div>
                            </div>

                            {/* Dynamic inputs as separate rows */}
                            <DynamicFieldsRenderer
                                fieldConfig={fieldConfig}
                                formKey={formKey}
                                formData={formData}
                                handleChange={handleChange}
                                dealDrawerState={dealDrawerState}
                            />
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};
